import { DateTime } from 'luxon'
import React, { useContext } from 'react'
import { Columns, Copy, Info, InfoTooltip, Link } from '../../components'
import { WemoloLoading, WemoloTypography } from '../../components/Wemolo'
import styles from './DeviceInfo.module.css'
import { DeviceTypeEnum, getPillFromDeviceType } from '../../data/enums'
import { getBalenaStatusIndicator } from '../../utils'
import { DeviceContext } from './DeviceContext'
import {
    getBalenaUrl,
    getCameraUrl,
    getDeviceReleaseStatus,
    getElasticLogsUrl,
    getRouterUrl,
    getTelktonikaRmsUrl,
    getWatchdogUrl
} from './utils'
import { getDeviceStatus, getStatusColor } from './utils/getDeviceStatus'
export const DeviceInfo = () => {
    const deviceContext = useContext(DeviceContext)

    const deviceType = deviceContext.getSelectedDeviceType()
    const links: { link: string | undefined | null; text: string }[] = [
        {
            link:
                deviceContext.selectedDevice?.uuid &&
                getBalenaUrl(deviceContext.selectedDevice?.uuid?.replace('balena_', '')),
            text: 'Balena'
        }
        // {
        //     link:
        //         deviceContext.selectedDevice?.uuid &&
        //         getCameraPublicUrl(deviceContext.selectedDevice?.uuid?.replace('balena_', '')),
        //     text: 'Camera (Public URL)'
        // },
    ]
    if (
        deviceType === DeviceTypeEnum.SCANNER ||
        deviceType === DeviceTypeEnum.TERMINAL ||
        deviceType === DeviceTypeEnum.DISPLAY
    ) {
        links.push({
            link: deviceContext.deviceData && getElasticLogsUrl(deviceContext.deviceData?.uuid || '', deviceType),
            text: 'Elastic Logs'
        })
        links.push({
            link: deviceContext.deviceData && getTelktonikaRmsUrl(deviceContext.deviceData?.device_name || ''),
            text: 'RMS'
        })
    }
    if (deviceType === DeviceTypeEnum.SCANNER) {
        links.push({
            link: deviceContext.deviceData && getWatchdogUrl(deviceContext.deviceData),
            text: 'Watchdog (VPN)'
        })
        links.push({
            link: deviceContext.deviceData && getCameraUrl(deviceContext.deviceData),
            text: 'Camera (VPN)'
        })
        links.push({
            link: deviceContext.deviceData && getRouterUrl(deviceContext.deviceData),
            text: 'Router (VPN)'
        })
    }

    // On a cold start with `?deviceId=` in the URL, the selection can't resolve until the device list
    // lands — without this every field reads as "-", as if the device had no data.
    if (deviceContext.loadingFetchDevices || deviceContext.loadingDeviceData) {
        return (
            <div className={styles.loading}>
                <WemoloLoading />
            </div>
        )
    }

    const deviceBalenaRawData = deviceContext.balenaDeviceData?.devices?.[0]

    const getToolTipItem = (title: string, value: any, copy = true) => (
        <div className={styles.tooltipItem}>
            <b> {title}</b> : &nbsp; {/* {deviceContext.runningCommit || '-'}{' '} */}
            {copy ? <Copy color="white" item={value} value={value} /> : value}
        </div>
    )
    return (
        <div className={styles.root}>
            <div className={styles.infoColumn}>
                <Columns>
                    <Info copyValue={true} label="Device Name" value={deviceContext.deviceData?.device_name || '-'} />
                    <Info
                        copyValue={false}
                        label="Device Type"
                        value={
                            <WemoloTypography variant="title-xsmall" tag="span" className={styles.inversePill}>
                                {getPillFromDeviceType(deviceContext.getSelectedDeviceType()) || '-'}
                            </WemoloTypography>
                        }
                    />

                    <Info copyValue={true} label="Device UUID" value={deviceContext.deviceData?.uuid || '-'} />

                    <Info
                        label={<>Device Configuration Status </>}
                        value={
                            deviceContext.deviceData ? getStatusColor(getDeviceStatus(deviceContext.deviceData)) : '-'
                        }
                    />
                    <Info
                        label="Device Release Status"
                        value={
                            deviceContext.deviceData ? (
                                <InfoTooltip
                                    content={
                                        <>
                                            {getToolTipItem('Release Target', deviceContext.targetCommit, false)}
                                            {getToolTipItem('Release Currently', deviceContext.runningCommit, false)}
                                            {getToolTipItem(
                                                'Supervisor Version',
                                                deviceBalenaRawData?.supervisor_version ?? '-',
                                                false
                                            )}
                                            {getToolTipItem(
                                                'Update Status',
                                                deviceBalenaRawData?.update_status ?? '-',
                                                false
                                            )}
                                        </>
                                    }
                                >
                                    {getStatusColor(getDeviceReleaseStatus(deviceContext.balenaDeviceData))}
                                </InfoTooltip>
                            ) : (
                                '-'
                            )
                        }
                    />
                    <Info
                        label="Device Balena Status"
                        value={
                            deviceContext.balenaStatus ? (
                                <div className={styles.balenaStatus}>
                                    <InfoTooltip
                                        content={
                                            <>
                                                {getToolTipItem(
                                                    'VPN Connected',
                                                    deviceBalenaRawData?.is_connected_to_vpn + '',
                                                    false
                                                )}
                                                {getToolTipItem(
                                                    'API Heartbeat State',
                                                    deviceBalenaRawData?.api_heartbeat_state + '',
                                                    false
                                                )}
                                                {getToolTipItem(
                                                    'Device Status',
                                                    deviceBalenaRawData?.status ?? '-',
                                                    false
                                                )}
                                                {getToolTipItem(
                                                    'Last connectivity event',
                                                    DateTime.fromISO(
                                                        deviceBalenaRawData?.last_connectivity_event ?? ''
                                                    ).toRelative({
                                                        locale: 'en'
                                                    }),
                                                    false
                                                )}
                                                {getToolTipItem(
                                                    'Last VPN event',
                                                    DateTime.fromISO(
                                                        deviceBalenaRawData?.last_vpn_event ?? ''
                                                    ).toRelative({
                                                        locale: 'en'
                                                    }),
                                                    false
                                                )}
                                            </>
                                        }
                                    >
                                        {getBalenaStatusIndicator(deviceBalenaRawData)}
                                    </InfoTooltip>
                                    {getBalenaStatusIndicator(deviceBalenaRawData)?.props?.children === 'OFFLINE' && (
                                        <div className={styles.balenaOfflineText}>
                                            {DateTime.fromISO(
                                                deviceBalenaRawData?.last_connectivity_event ?? ''
                                            ).toRelative({
                                                locale: 'en'
                                            })}
                                        </div>
                                    )}
                                </div>
                            ) : (
                                '-'
                            )
                        }
                    />

                    {/* <Info copyValue={true} label="Release Target : " value={deviceContext.targetCommit || '-'} />
                    <Info copyValue={true} label="Release Currently : " value={deviceContext.runningCommit || '-'} /> */}
                    <Info
                        label="Lot"
                        value={
                            <div>
                                {[
                                    //... new Set will remove duplicates from an array since it only allows unique values
                                    ...new Set(
                                        deviceContext.deviceStations?.map((device) => device.station_name.split('_')[0])
                                    )
                                ].map((lotId) => (
                                    <div key={lotId}>
                                        <Link link={`/lot?lot=${lotId}`} target="_self" text={lotId} />
                                        &nbsp; &nbsp;
                                    </div>
                                ))}
                                {(!deviceContext.deviceStations || deviceContext.deviceStations?.length < 1) && '-'}
                            </div>
                        }
                    />

                    <Info
                        label="Stations"
                        value={
                            <div>
                                {deviceContext.deviceStations?.map((device) => {
                                    return (
                                        <div key={device.station_name}>
                                            <Link
                                                link={`/station?stationName=${device.station_name}`}
                                                target="_self"
                                                text={device.station_name}
                                            />
                                            &nbsp; &nbsp;
                                        </div>
                                    )
                                })}
                                {(!deviceContext.deviceStations || deviceContext.deviceStations?.length < 1) && '-'}
                            </div>
                        }
                    />
                </Columns>
            </div>

            <div className={styles.linksColumn}>
                <Columns>
                    <div className={styles.linksList}>
                        {links.map(({ link, text }, index) => (
                            <div key={index} className={styles.linkItem}>
                                {link && <Link key={index} link={link} text={text} />}
                            </div>
                        ))}
                    </div>
                </Columns>
            </div>
            {/* {deviceContext.deviceData?.uuid && <SendCommand />} */}
        </div>
    )
}
